import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { userApi } from '../services/api';
import { User, AccountUsage } from '../types';

interface UserContextType {
  userProfile: User | null;
  /** Storage and chatbot usage against the plan. Null until loaded, and null
   *  if the call failed — callers render nothing rather than a wrong number. */
  usage: AccountUsage | null;
  isLoading: boolean;
  refreshProfile: () => Promise<void>;
  refreshUsage: () => Promise<void>;
  deductCredits: (amount: number) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [usage, setUsage] = useState<AccountUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshProfile = useCallback(async () => {
    try {
      const response = await userApi.getProfile();
      if (response.success && response.data) {
        setUserProfile(response.data);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Usage is fetched separately from the profile and failure is swallowed to
   * null. It reads a DynamoDB ledger and can answer 503 when limits are
   * unavailable, and a storage figure is worth strictly less than the rest of
   * the header — showing nothing beats blocking the page or, worse, rendering
   * a wrong allowance.
   */
  const refreshUsage = useCallback(async () => {
    try {
      const response = await userApi.getUsage();
      setUsage(response.success && response.data ? response.data : null);
    } catch (error) {
      console.error('Error fetching account usage:', error);
      setUsage(null);
    }
  }, []);

  // Deduct credits locally (optimistic update)
  const deductCredits = useCallback((amount: number) => {
    setUserProfile(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        balance: Math.max(0, (prev.balance || 0) - amount)
      };
    });
  }, []);

  // Load profile on mount
  useEffect(() => {
    refreshProfile();
    refreshUsage();
  }, [refreshProfile, refreshUsage]);

  return (
    <UserContext.Provider
      value={{ userProfile, usage, isLoading, refreshProfile, refreshUsage, deductCredits }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
